import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import ImagePreview from '@/components/common/media/ImagePreview';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  ICollection,
  ICollectionResponse,
  ICollectionState,
} from '@/interfaces/collection.interface';
import { ActionConfig, ICommonOption } from '@/interfaces/common.interface';
import {
  collectionOption,
  GetFilterUrl,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { PlusSquare } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Collection: React.FC = () => {
  const navigate = useNavigate();
  const { canView, canEdit, canCreate } = usePermissions();
  const [searchValue, setSearchValue] = useState<string>('');
  const debounceValue = useDebounce(searchValue);
  const [state, setState] = useState<ICollectionState>({
    collection: [],
    pageInfo: {
      total: 1,
      totalPages: 1,
      limit: 10,
      page: 1,
    },
    isLoading: false,
    selectedCollectionId: null,
    showUpdateModel: false,
    statusToUpdate: null,
    isActive: '',
    orderDir: 'DESC',
  });
  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(state.collection);

  const collectionFilters: ICommonOption[] = [
    {
      value: '',
      label: 'All',
      textClass: 'text-green-500',
      bgClass: 'bg-green-500',
    },
    {
      value: 'true',
      label: 'Active',
      textClass: 'text-green-500',
      bgClass: 'bg-green-500',
    },
    {
      value: 'false',
      label: 'Inactive',
      textClass: 'text-green-500',
      bgClass: 'bg-green-500',
    },
  ];

  const collectionColumns: TableColumn<ICollection>[] = [
    {
      key: 'title',
      header: 'Title',
      render: (collection) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${collection?.media?.[0]?.url}`}
          alt={''}
          text={collection.title}
        />
      ),
      className: 'p-3',
    },
    {
      key: 'price',
      header: 'Price (BHD)',
      render: (collection) => toFixedThree(collection.price),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.isActive ? 'active' : 'inactive'}
          options={collectionOption}
          onChange={(status) => {
            setState((prev) => ({
              ...prev,
              showUpdateModel: true,
              selectedCollectionId: item.id,
              statusToUpdate: status,
            }));
          }}
          canEditVal="product"
        />
      ),
    },
    {
      key: 'products',
      header: 'Products',
      render: (order) => `${order.items.length}`,
    },
  ];

  const collectionActionConfig: Record<string, ActionConfig<ICollection>> = {
    view: {
      text: 'View',
      onClick: (collection) =>
        navigate(`${RoutesLink.collectionDetails}/${collection.id}`),
      permission: () => canView('collection'),
    },
    eidt: {
      text: 'Edit',
      onClick: (collection) =>
        navigate(`${RoutesLink.createCollection}/${collection.id}`),
      permission: () => canEdit('collection'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(collectionActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit],
  );

  useEffect(() => {
    fetchCollection();
  }, [
    debounceValue,
    state.pageInfo.limit,
    state.pageInfo.page,
    state.isActive,
    state.orderDir,
  ]);

  const onStatusUpdate = async (id: string, status: string) => {
    try {
      setState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.bundles}/${id}`;
      const payload = {
        isActive: status === 'active' ? true : false,
      };
      await callApi(PATH, { method: 'PATCH', data: payload });
      fetchCollection();
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const fetchCollection = async () => {
    try {
      setState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const filterQuery = {
        page: state.pageInfo.page,
        limit: state.pageInfo.limit,
        search: debounceValue,
        isActive: state.isActive,
        orderDir: state.orderDir,
      };
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.bundles}?${query ? `${query}` : ''}`;
      const res = await callApi<ICollectionResponse>(PATH, { method: 'GET' });
      setState((prev) => ({
        ...prev,
        collection: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleChangePage = (page: number) => {
    setState((prev) => ({
      ...prev,
      pageInfo: {
        ...prev.pageInfo,
        page: page,
      },
    }));
  };

  const handleChangePageSize = (limit: number) => {
    setState((prev) => ({
      ...prev,
      pageInfo: {
        ...prev.pageInfo,
        page: 1,
        limit: limit,
      },
    }));
  };

  const filterOption = [
    { value: 'DESC', label: 'Newest First' },
    { value: 'ASC', label: 'Oldest First' },
  ];

  return (
    <Layout>
      <PageHeading
        text="Collection"
        button={
          canCreate('collection')
            ? {
                icon: <PlusSquare />,
                text: 'Add Collection',
                onClick: () => navigate(RoutesLink.createCollection),
              }
            : undefined
        }
      />
      <Card>
        <CommonFilter
          filters={collectionFilters}
          onChange={(filter) => {
            setState((prev) => ({
              ...prev,
              isActive: filter,
              pageInfo: { ...state.pageInfo, page: 1 },
            }));
          }}
          filterOptions={{
            values: filterOption,
            onChange: (value) =>
              setState((prev) => ({ ...prev, orderDir: value })),
          }}
          inputPlaceHolder="Search for Collection..."
          onSearch={(text) => setSearchValue(text)}
        />
        <CommonTable
          columns={collectionColumns}
          data={state.collection}
          showCheckbox={false}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Collections found"
          loading={state.isLoading}
          actions={actions}
        />
        {state.pageInfo.totalPages > 1 && (
          <Pagination
            isLoading={state.isLoading}
            paginationData={state.pageInfo}
            onPageChange={(page) => handleChangePage(page)}
            onPageSizeChange={handleChangePageSize}
          />
        )}
        {state.showUpdateModel && (
          <ConfirmationModel
            modelType="update"
            show={state.showUpdateModel}
            onClose={() => {
              setState((prev) => ({
                ...prev,
                showUpdateModel: false,
                selectedCollectionId: null,
                statusToUpdate: null,
              }));
            }}
            subTitle={`This action will update the status to ${state.statusToUpdate}`}
            onConfirm={() =>
              onStatusUpdate(
                state.selectedCollectionId as string,
                state.statusToUpdate as string,
              )
            }
          />
        )}
      </Card>
    </Layout>
  );
};

export default Collection;
